import { GlobalStyles } from "@mui/material";
import Button from "@mui/material/Button";
import Grid2 from "@mui/material/Grid2";
import { AgGridReact } from "ag-grid-react";
import {
	type ColDef,
	type DefaultMenuItem,
	type GetContextMenuItemsParams,
	type GridApi,
	type MenuItemDef,
	type RowClickedEvent,
	RowDoubleClickedEvent,
} from "ag-grid-community";
import "ag-grid-enterprise";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useFormsContext } from "../../context/FormsContext";
import { LazyLoading } from "@/components/loaders/Loader/LazyLoading";
import { ErrorModal, MesfModal } from "@dexteel/mesf-core";
import { ModalGridBox, listMenuColumn } from "../modal-grid";
import { AssetNameColumn } from "./components/columns-table/assetName-column";
import { useSearchFormAssets } from "../../hooks/useSearchFormAssets";
import type { FormAsset } from "../../models/FormAsset";
import { UsersFormAssetTable } from "./usersFormAssetTable";
import { DeleteFormAssetModal } from "./components/delete-form-asset-modal/delete-form-asset-modal";
import { NewFormAssetModal } from "./components/new-form-asset-modal/new-form-asset-modal";
import { INITIAL_VALUES_FORM_ASSET } from "../../models/FormAssetInitialValue";
import { globalDefaultColDef } from "@setup/ag-grid/mesf-theme";

type Props = {
	show: boolean;
	onHide: (onHide: boolean) => void;
	setOpenAddFormAssetAndUserModal: Function;
	openAddFormAssetAndUserModal: boolean;
};

export const FormAssetsAndUsersTable = ({
	show,
	onHide,
	setOpenAddFormAssetAndUserModal,
	openAddFormAssetAndUserModal,
}: Props) => {
	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const [columnDefs, setColumnDefs] = useState<ColDef[]>([]);
	const [shouldUpdateWhenExit, setShouldUpdateWhenExit] = useState(false);
	const [formAssetId, setFormAssetId] = useState<number | null>(null);
	const [assetToRemoveName, setAssetToRemoveName] = useState("");
	const [openDeleteFormAssetModal, setOpenDeleteFormAssetModal] =
		useState(false);

	const [openNewEditUserFormAsset, setOpenNewEditUserFormAsset] =
		useState(false);
	const [openDeleteUserFormAsset, setOpenDeleteUserFormAsset] = useState(false);
	const [newUserFormAssetMode, setNewUserFormAssetMode] =
		useState<boolean>(false);

	const {
		state: { FormName, formAssetsList },
		actions: {
			setFormAssetsList,
			setFormId,
			setAssetId,
			setUsersFormAssetList,
			setFormAsset,
		},
	} = useFormsContext();

	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [error, setError] = useState<string>("");

	const searchFormAssets = useSearchFormAssets({ setIsLoading, setError });

	const loadingOverlayComponent = useMemo<any>(() => {
		return LazyLoading;
	}, []);

	const rowClicked = (event: RowClickedEvent) => {
		if (event.data) {
			setFormAssetId(event.data.FormAssetId);
		}
	};

	const handleCancel = () => {
		onHide(shouldUpdateWhenExit);
		setFormAssetId(null);
		setFormAsset(INITIAL_VALUES_FORM_ASSET);
	};

	const getFormAssetContextMenuItems = useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			const data: FormAsset = params.node?.data;
			const result: (DefaultMenuItem | MenuItemDef)[] = [];

			if (data) {
				result.push({
					name: "Add Asset",
					action: () => {
						setFormId(data?.FormId);
						setOpenAddFormAssetAndUserModal(true);
					},
					icon: '<i class="fas fa-plus"></i>',
				});
			}
			if (data) {
				result.push("separator", {
					name: "Remove Asset",
					action: () => {
						setFormId(data?.FormId);
						setAssetId(data?.AssetId);
						setAssetToRemoveName(data?.AssetName ?? "");
						setOpenDeleteFormAssetModal(true);
					},
					icon: '<i class="fas fa-trash"></i>',
				});
			}

			const addNewForm: (DefaultMenuItem | MenuItemDef)[] = [];

			if (!data) {
				addNewForm.push({
					name: "Add Asset",
					action: () => {
						setOpenAddFormAssetAndUserModal(true);
					},
					icon: '<i class="fas fa-plus"></i>',
				});
			}

			return data ? result : addNewForm;
		},
		[],
	);

	useEffect(() => {
		if (show) {
			setShouldUpdateWhenExit(false);
			searchFormAssets();
			setColumnDefs([]);
			setFormAssetsList([]);
			setFormAssetId(null);

			const newColumns: ColDef[] = [];

			newColumns.push({
				headerName: "Asset",
				sortable: false,
				flex: 7,
				minWidth: 130,
				cellRenderer: AssetNameColumn,
			});
			newColumns.push(listMenuColumn);

			setColumnDefs(newColumns);
		} else setUsersFormAssetList([]);
	}, [show]);

	useEffect(() => {
		if (isLoading) {
			gridApi?.showLoadingOverlay();
		} else gridApi?.hideOverlay();
	}, [isLoading, gridApi]);

	return (
		<>
			<GlobalStyles
				styles={{
					".MuiDialog-paper[aria-labelledby='configure-form-modal']": {
						width: "100%",
					},
				}}
			/>
			<MesfModal
				title={`CONFIGURE FORM - ${FormName ?? ""}`}
				open={show}
				handleClose={handleCancel}
				maxWidth="lg"
				id="configure-form-modal"
			>
				<MesfModal.Content dividers>
					<Grid2 container spacing={2}>
						<Grid2 size={{ md: 4, xs: 12 }}>
							<ModalGridBox
								height="55vh"
								onAdd={() => setOpenAddFormAssetAndUserModal(true)}
							>
								<div style={{ height: "100%", width: "100%" }}>
									<AgGridReact
										rowData={formAssetsList}
										columnDefs={columnDefs}
										defaultColDef={globalDefaultColDef}
										rowHeight={38}
										headerHeight={42}
										loadingOverlayComponent={loadingOverlayComponent}
										animateRows={true}
										getContextMenuItems={(e: any) =>
											getFormAssetContextMenuItems(e)
										}
										onRowClicked={rowClicked}
										rowSelection="single"
										suppressCellFocus={true}
										onGridReady={(params) => setGridApi(params.api)}
									/>
								</div>
							</ModalGridBox>
						</Grid2>
						<UsersFormAssetTable
							FormAssetIdToSearchUserFormAssets={formAssetId as number}
							openNewEditUserFormAsset={openNewEditUserFormAsset}
							openDeleteUserFormAsset={openDeleteUserFormAsset}
							setOpenNewEditUserFormAsset={setOpenNewEditUserFormAsset}
							setOpenDeleteUserFormAsset={setOpenDeleteUserFormAsset}
							newUserFormAssetMode={newUserFormAssetMode}
							setNewUserFormAssetMode={setNewUserFormAssetMode}
							setShouldUpdateWhenExit={setShouldUpdateWhenExit}
						/>
					</Grid2>
				</MesfModal.Content>
				<MesfModal.Actions style={{ padding: "20px 16px 16px" }}>
					<Grid2 container spacing={2} justifyContent="flex-end" width="100%">
						<Grid2 size={{ md: 3, xs: 12 }}>
							<Button
								fullWidth
								variant="contained"
								color="inherit"
								onClick={handleCancel}
							>
								Close
							</Button>
						</Grid2>
					</Grid2>
				</MesfModal.Actions>
			</MesfModal>
			<NewFormAssetModal
				show={openAddFormAssetAndUserModal}
				onHide={(shouldUpdate: boolean) => {
					if (shouldUpdate) {
						searchFormAssets();
						setShouldUpdateWhenExit(true);
						setFormAssetId(null);
					}
					setOpenAddFormAssetAndUserModal(false);
				}}
			/>
			<DeleteFormAssetModal
				show={openDeleteFormAssetModal}
				assetName={assetToRemoveName}
				onHide={(shouldUpdate: boolean) => {
					if (shouldUpdate) {
						searchFormAssets();
						setShouldUpdateWhenExit(true);
						setFormAssetId(null);
					}
					setOpenDeleteFormAssetModal(false);
				}}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
